import { useSignUp } from "@clerk/tanstack-react-start";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/motion/button";
import { DEFAULT_POST_AUTH_REDIRECT, validateAuthRedirectSearch } from "@/lib/auth-routing";
import { authorizeRequestFromSearch } from "@/lib/connect-partner";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/login/complete/")({
  validateSearch: validateAuthRedirectSearch,
  head: () =>
    seoHead({
      title: "Finish creating your account - Bavimail",
      description: "One more step to create your Bavimail account.",
      path: "/login/complete/",
      noindex: true,
    }),
  component: CompleteSignUpRoute,
});

// A new user who arrived through a partner's identity provider: Clerk already holds their
// verified email and created the sign-up; the instance requires legal consent (and may ask for
// a name) before the account exists. Nothing pending means there is nothing to finish here.
function CompleteSignUpRoute() {
  const { redirect_url } = Route.useSearch();
  const { signUp } = useSignUp();
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const destination = authorizeRequestFromSearch(redirect_url)?.href ?? DEFAULT_POST_AUTH_REDIRECT;

  if (signUp.status !== "missing_requirements") {
    return <Navigate to="/login" search={{ redirect_url }} replace />;
  }

  const asksForName =
    signUp.missingFields.includes("first_name") || signUp.missingFields.includes("last_name");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setFailure(null);
    const updated = await signUp.update({
      legalAccepted: true,
      ...(asksForName ? { firstName: firstName.trim(), lastName: lastName.trim() } : {}),
    });
    if (updated.error) {
      setFailure(updated.error.message);
      setSubmitting(false);
      return;
    }
    if (signUp.status !== "complete") {
      setFailure("Your account still needs something we could not collect here.");
      setSubmitting(false);
      return;
    }
    const finalized = await signUp.finalize({
      navigate: ({ decorateUrl }) => {
        window.location.assign(decorateUrl(destination));
      },
    });
    if (finalized.error) {
      setFailure(finalized.error.message);
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#0d0d0f] px-4 py-10">
      <form
        onSubmit={(event) => void submit(event)}
        className="w-full max-w-md border border-dashed border-[#2a2a2e] bg-[#111114] p-8"
      >
        <p className="font-mono-hb text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a]">
          Connect Bavimail
        </p>
        <h1 className="font-hb mt-3 text-2xl font-extrabold leading-tight text-white">
          One more step
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#d0d0d0]">
          Your Bavimail account is being created
          {signUp.emailAddress ? (
            <>
              {" "}
              for <span className="font-mono-hb text-white">{signUp.emailAddress}</span>
            </>
          ) : null}
          .
        </p>
        {asksForName ? (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <label className="block text-xs text-[#8a8a8a]">
              First name
              <input
                className="mt-1 w-full border border-[#2a2a2e] bg-[#0a0a0c] px-3 py-2 text-sm text-white outline-none focus:border-[#444444]"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="block text-xs text-[#8a8a8a]">
              Last name
              <input
                className="mt-1 w-full border border-[#2a2a2e] bg-[#0a0a0c] px-3 py-2 text-sm text-white outline-none focus:border-[#444444]"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                autoComplete="family-name"
                required
              />
            </label>
          </div>
        ) : null}
        <label className="mt-6 flex items-start gap-3 text-sm leading-6 text-[#d0d0d0]">
          <input
            type="checkbox"
            className="mt-1 size-4 accent-[#f6821f]"
            checked={legalAccepted}
            onChange={(event) => setLegalAccepted(event.target.checked)}
            required
          />
          <span>
            I agree to the{" "}
            <Link to="/terms" className="text-white underline underline-offset-4">
              Terms of Service
            </Link>{" "}
            and the{" "}
            <Link to="/privacy" className="text-white underline underline-offset-4">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        {failure === null ? null : (
          <p className="mt-4 text-sm text-[#ef4444]" aria-live="polite">
            {failure}
          </p>
        )}
        <div id="clerk-captcha" className="mt-4" />
        <Button size="lg" type="submit" className="mt-6" disabled={!legalAccepted || submitting}>
          {submitting ? "Creating your account…" : "Create account and continue"}
        </Button>
      </form>
    </main>
  );
}
